import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../../../common/components/xepp/button/Button";
import { ApplicationRoutes } from "../../../../../routes";
import { useGetDepartmentsQuery, useDeleteDepartmentMutation } from "../../../../../service/department-service";
import ConfirmationModal from "../../../../../common/components/xepp/confirmation-modal/ConfirmationModal";
import styles from "./DepartmentsTab.module.scss";

// Helper function to generate avatar color based on department name
const getAvatarColor = (name: string) => {
	const colors = ["#EF4444", "#EC4899", "#10B981", "#6B7280", "#3B82F6", "#F59E0B", "#8B5CF6", "#14B8A6"];
	const index = name.charCodeAt(0) % colors.length;
	return colors[index];
};

const DepartmentsTab = memo(() => {
	const navigate = useNavigate();
	const { data: departments, isLoading, error } = useGetDepartmentsQuery();
	const [deleteDepartment, { isLoading: isDeleting }] = useDeleteDepartmentMutation();
	const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; departmentId: string; departmentName: string }>({
		isOpen: false,
		departmentId: "",
		departmentName: ""
	});

	const handleDeleteClick = (departmentId: string, departmentName: string) => {
		setDeleteModal({
			isOpen: true,
			departmentId,
			departmentName
		});
	};

	const handleConfirmDelete = async () => {
		try {
			await deleteDepartment(deleteModal.departmentId).unwrap();
			setDeleteModal({ isOpen: false, departmentId: "", departmentName: "" });
		} catch (error) {
			console.error("Failed to delete department:", error);
		}
	};

	const handleCloseModal = () => {
		setDeleteModal({ isOpen: false, departmentId: "", departmentName: "" });
	};

	if (isLoading) {
		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<h2 className={styles.title}>Departments</h2>
				</div>
				<div style={{ textAlign: 'center', padding: '2rem' }}>Loading departments...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<h2 className={styles.title}>Departments</h2>
				</div>
				<div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
					Error loading departments. Please try again.
				</div>
			</div>
		);
	}

	console.log('Departments:', departments);

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h2 className={styles.title}>Departments</h2>
				<Button
					text="Add New Department"
					variant="primary"
					onClick={() => navigate(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}/${ApplicationRoutes.DEPARTMENT_CONFIGURATION}/${ApplicationRoutes.CREATE_DEPARTMENT}`)}
				/>
			</div>

			<div className={styles.departmentGrid}>
				{departments?.map((dept) => (
					<div key={dept.id} className={styles.departmentCard}>
						<div className={styles.cardHeader}>
							<div className={styles.departmentInfo}>
								<div
									className={styles.avatar}
									style={{ backgroundColor: getAvatarColor(dept.name) }}
								>
									{dept.name.charAt(0).toUpperCase()}
								</div>
								<h3 className={styles.departmentName}>{dept.name}</h3>
							</div>
							<button className={styles.menuButton}>
								<span>⋮</span>
							</button>
						</div>
						<p className={styles.description}>{dept.description}</p>
						<button
							className={styles.deleteButton}
							onClick={() => handleDeleteClick(dept.id, dept.name)}
						>
							Delete
						</button>
						<div className={styles.peopleCount}>
							{dept.peopleCount} {dept.peopleCount === 1 ? 'Person' : 'People'}
						</div>
					</div>
				))}
			</div>

			<ConfirmationModal
				isOpen={deleteModal.isOpen}
				onClose={handleCloseModal}
				onConfirm={handleConfirmDelete}
				title="Delete Department"
				message={`Are you sure you want to delete "${deleteModal.departmentName}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				isLoading={isDeleting}
				variant="danger"
			/>
		</div>
	);
});

export default DepartmentsTab;
